import { useCallback, useEffect, useState } from 'react';
import { agentSessionsService, type AgentSessionDTO } from '@/services/roros/agent-sessions.service';

export function useAgentSessionList() {
  const [sessions, setSessions] = useState<AgentSessionDTO[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [sidebarSearch, setSidebarSearch] = useState('');
  const [debouncedSidebarSearch, setDebouncedSidebarSearch] = useState('');

  // AC-CHAT-7: debounce sidebar search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSidebarSearch(sidebarSearch), 300);
    return () => clearTimeout(timer);
  }, [sidebarSearch]);

  // Load sessions list
  const loadSessions = useCallback(async (search?: string) => {
    setIsLoadingSessions(true);
    try {
      const result = await agentSessionsService.listSessions(search || undefined);
      setSessions(result ?? []);
    } catch {
      // non-fatal
    } finally {
      setIsLoadingSessions(false);
    }
  }, []);

  // Load sessions on mount + when sidebar search changes
  useEffect(() => {
    loadSessions(debouncedSidebarSearch || undefined);
  }, [loadSessions, debouncedSidebarSearch]);

  const prependSession = useCallback((session: AgentSessionDTO) => {
    setSessions((prev) => [session, ...prev]);
  }, []);

  const replaceSession = useCallback((session: AgentSessionDTO) => {
    setSessions((prev) => prev.map((item) => (item.id === session.id ? session : item)));
  }, []);

  return {
    sessions,
    isLoadingSessions,
    sidebarSearch,
    setSidebarSearch,
    prependSession,
    replaceSession,
  };
}
